import React from "react";
import { motion, useReducedMotion } from "framer-motion";

// Standard easing used across the whole site — smooth "material" curve.
export const EASE = [0.22, 1, 0.36, 1];

/**
 * Scroll-reveal wrapper with sensible defaults + variants.
 * Usage: <Reveal><MyContent /></Reveal>
 * Direction: "up" | "down" | "left" | "right" | "fade" | "scale"
 */
const Reveal = ({
  children,
  direction = "up",
  delay = 0,
  duration = 0.85,
  distance = 48,
  once = true,
  amount = 0.2,
  as,
  ...rest
}) => {
  const reduce = useReducedMotion();

  const initial = reduce
    ? { opacity: 0 }
    : (() => {
        switch (direction) {
          case "down":
            return { opacity: 0, y: -distance };
          case "left":
            return { opacity: 0, x: -distance };
          case "right":
            return { opacity: 0, x: distance };
          case "scale":
            return { opacity: 0, scale: 0.92 };
          case "fade":
            return { opacity: 0 };
          case "up":
          default:
            return { opacity: 0, y: distance };
        }
      })();

  const whileInView = reduce
    ? { opacity: 1 }
    : { opacity: 1, x: 0, y: 0, scale: 1 };

  const Comp = as || motion.div;

  return (
    <Comp
      initial={initial}
      whileInView={whileInView}
      viewport={{ once, amount }}
      transition={{ duration, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Comp>
  );
};

/**
 * Stagger container — direct children animate in sequence.
 */
export const Stagger = ({
  children,
  delayChildren = 0.1,
  staggerChildren = 0.09,
  ...rest
}) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.2 }}
    variants={{
      hidden: {},
      show: { transition: { delayChildren, staggerChildren } },
    }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, y = 28, ...rest }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y },
      show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.75, ease: EASE },
      },
    }}
    {...rest}
  >
    {children}
  </motion.div>
);

export default Reveal;
